import React, { useState, useEffect, useCallback } from 'react';
import type { ErrorInfo } from 'react';
import { ExperimentExportJiraContextToPageNudgeSpotlightTour } from './ui/spotlight-tour';
import {
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { trackSpotlightEvent, trackErrorEvent } from './common/analytics';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

const useExportJiraContextToPageNudgeQueryParam = createSingleQueryParamHook('export_source');
const useResetTourQueryParam = createSingleQueryParamHook('resetTour');

export const ExportJiraContextToPageNudge = ({ children }: { children: React.ReactNode }) => {
	const exportSource = useExportJiraContextToPageNudgeQueryParam();
	const resetTour = useResetTourQueryParam();

	const isEligible = exportSource === 'jiraTimelineExport';

	// Check if tour has been completed or dismissed
	const {
		data: tourStateData,
		loading: tourStateLoading,
		error: tourStateError,
	} = useGetOnboardingState([
		EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_COMPLETED,
		EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_DISMISSED,
	]);
	const { setOnboardingState } = useSetOnboardingState();

	const hasCompletedTour =
		tourStateData?.onboardingState?.find(
			(prop) => prop.key === EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_COMPLETED,
		)?.value === 'true';

	const hasDismissedTour =
		tourStateData?.onboardingState?.find(
			(prop) => prop.key === EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_DISMISSED,
		)?.value === 'true';

	const isOnboardingStateLoaded = Boolean(!tourStateLoading && !tourStateError && tourStateData);

	// Reset tour state if resetTour=1 query parameter is present and user is eligible
	useEffect(() => {
		if (resetTour === '1' && isEligible && isOnboardingStateLoaded) {
			setOnboardingState({
				key: EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_COMPLETED,
				value: 'false',
			});
			setOnboardingState({
				key: EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_DISMISSED,
				value: 'false',
			});
		}
	}, [resetTour, isEligible, isOnboardingStateLoaded, setOnboardingState]);

	// Only show tour if eligible, onboarding state is loaded, and tour hasn't been completed or dismissed
	// If resetTour=1, we ignore the completed/dismissed state to allow the tour to show
	const shouldShowTour =
		isEligible &&
		isOnboardingStateLoaded &&
		(resetTour === '1' || (!hasCompletedTour && !hasDismissedTour));
	const [shouldStartTour, setShouldStartTour] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// Delay tour start to ensure DOM targets are ready
		if (shouldShowTour) {
			setShouldStartTour(true);
			// Track that spotlight started
			trackSpotlightEvent({
				createAnalyticsEvent,
				action: 'started',
				step: 0, // Starting at step 0
			});
		} else if (isEligible && isOnboardingStateLoaded && hasCompletedTour) {
			// Track skipped event for eligible users who already completed
			trackSpotlightEvent({
				createAnalyticsEvent,
				action: 'skipped',
				step: null,
			});
		}
	}, [shouldShowTour, isEligible, isOnboardingStateLoaded, hasCompletedTour, createAnalyticsEvent]);

	const handleTourComplete = () => {
		setOnboardingState({
			key: EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_COMPLETED,
			value: 'true',
		});
	};

	const handleTourDismiss = () => {
		setOnboardingState({
			key: EXPORT_JIRA_CONTEXT_TO_PAGE_TOUR.TOUR_DISMISSED,
			value: 'true',
		});
	};

	// Handle errors and send operational event
	const handleError = useCallback(
		(error: Error, errorInfo: ErrorInfo) => {
			trackErrorEvent({
				createAnalyticsEvent,
				error,
				errorInfo,
				componentName: 'export-jira-context-nudge',
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<GenericErrorBoundary
			attribution={Attribution.INTEGRATIONS}
			renderOnError={() => children}
			onError={handleError}
		>
			{isEligible ? (
				<ExperimentExportJiraContextToPageNudgeSpotlightTour
					shouldShowTour={shouldStartTour && shouldShowTour}
					isEnrolled={isEligible}
					onTourComplete={handleTourComplete}
					onTourDismiss={handleTourDismiss}
				>
					{children}
				</ExperimentExportJiraContextToPageNudgeSpotlightTour>
			) : (
				children
			)}
		</GenericErrorBoundary>
	);
};
